import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ArrowUpRight, DollarSign, Mail, MessageCircle, Users, Calendar } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, BarChart, Bar, CartesianGrid } from "recharts";

const fmt = (n) => (n >= 1000 ? `${(n / 1000).toFixed(1)}k` : n);

export default function Overview() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/analytics/overview").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const cards = data
    ? [
        { icon: DollarSign, label: "Pipeline generated", value: `$${(data.pipeline_generated / 1000).toFixed(1)}k`, delta: "+18.2%", color: "text-emerald-300" },
        { icon: Calendar, label: "Meetings booked", value: data.meetings_booked, delta: "+9 this wk", color: "text-sky-300" },
        { icon: MessageCircle, label: "Reply rate", value: `${data.reply_rate}%`, delta: "+2.1 pts", color: "text-violet-300" },
        { icon: Mail, label: "Emails sent", value: fmt(data.emails_sent), delta: "+1.2k / wk", color: "text-amber-300" },
      ]
    : [];

  return (
    <div className="space-y-6" data-testid="overview-view">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Pipeline overview</h1>
          <p className="mt-1 text-sm text-slate-400">Live signal from your AI BDRs across email and LinkedIn.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.label} className="rounded-2xl border border-white/5 bg-white/[0.02] p-5" data-testid={`overview-card-${c.label.replace(/\s+/g, "-").toLowerCase()}`}>
            <div className="flex items-center justify-between">
              <c.icon className={`w-4 h-4 ${c.color}`} />
              <span className="text-[11px] font-mono text-emerald-300 flex items-center gap-1"><ArrowUpRight className="w-3 h-3" />{c.delta}</span>
            </div>
            <div className="mt-4 text-3xl font-bold">{c.value}</div>
            <div className="mt-1 text-xs uppercase tracking-widest text-slate-500">{c.label}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-2xl border border-white/5 bg-white/[0.02] p-6">
          <div className="text-sm font-semibold mb-4">Weekly volume & replies</div>
          <div className="h-64">
            {data && (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data.weekly}>
                  <CartesianGrid stroke="rgba(148,163,184,0.08)" vertical={false} />
                  <XAxis dataKey="week" stroke="#64748b" fontSize={12} />
                  <YAxis stroke="#64748b" fontSize={12} />
                  <Tooltip contentStyle={{ background: "#0B1020", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12 }} />
                  <Line type="monotone" dataKey="sent" stroke="#0EA5E9" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="replies" stroke="#10B981" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="meetings" stroke="#8B5CF6" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="rounded-2xl border border-white/5 bg-white/[0.02] p-6">
          <div className="text-sm font-semibold mb-4">Conversion funnel</div>
          <div className="h-64">
            {data && (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.funnel} layout="vertical" margin={{ left: 10 }}>
                  <XAxis type="number" stroke="#64748b" fontSize={11} />
                  <YAxis type="category" dataKey="stage" stroke="#64748b" fontSize={12} width={70} />
                  <Tooltip contentStyle={{ background: "#0B1020", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12 }} />
                  <Bar dataKey="count" fill="#0EA5E9" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      {data && (
        <div className="grid md:grid-cols-3 gap-4">
          {[
            { icon: Users, label: "Active agents", value: data.agents },
            { icon: Mail, label: "Leads in pipeline", value: data.leads },
            { icon: MessageCircle, label: "Sequences running", value: data.sequences },
          ].map((s) => (
            <div key={s.label} className="rounded-2xl border border-white/5 bg-white/[0.02] p-5 flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500">{s.label}</div>
                <div className="mt-1 text-2xl font-bold">{s.value}</div>
              </div>
              <s.icon className="w-5 h-5 text-slate-500" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
